import type { SdkMarketingAttribution } from '@ekhodealer/ekho-common';

import { appendAttribution, withLiveIdentity } from './client';

export interface LinkDecorationConfig {
  attribution: SdkMarketingAttribution | null;
}

/**
 * Hostnames we decorate on click: Ekho storefronts (`shop.<dealer>.…` /
 * `<dealer>-shop.ekho.com`) and checkout hosts (`checkout.<dealer>.…` /
 * `<dealer>-checkout.ekho.com`) — the latter so preexisting configurator static
 * links that point at checkout domains carry attribution. Matched by shape so we
 * do not maintain a host list.
 */
export function isEkhoStorefrontHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return (
    host.startsWith('shop.') ||
    host.includes('-shop.') ||
    host.startsWith('checkout.') ||
    host.includes('-checkout.')
  );
}

/**
 * Click-time link decoration: rewrite anchors whose href points at an Ekho
 * storefront or checkout host so the handoff carries live attribution + identity
 * IDs. Capture-phase so we run before navigation; URL-wins on existing params.
 */
export function installLinkDecoration(config: LinkDecorationConfig): () => void {
  if (typeof document === 'undefined') {
    return () => {};
  }

  const decorateAnchor = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;

    let parsed: URL;
    try {
      parsed = new URL(anchor.href, window.location.href);
    } catch {
      return;
    }

    if (!isEkhoStorefrontHostname(parsed.hostname)) return;

    const decorated = appendAttribution(parsed.toString(), withLiveIdentity(config.attribution));
    if (decorated !== anchor.href) {
      anchor.href = decorated;
    }
  };

  document.addEventListener('click', decorateAnchor, true);
  document.addEventListener('auxclick', decorateAnchor, true);

  return (): void => {
    document.removeEventListener('click', decorateAnchor, true);
    document.removeEventListener('auxclick', decorateAnchor, true);
  };
}
